
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestGeneralContractorsPage = () => {
  const serviceType = "General Contractors";
  const pageTitle = `Best ${serviceType} in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'general'],
    queryFn: async () => {
      // First try with "General Contractors"
      const { data: contractorsData, error: contractorsError } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'General Contractors')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (contractorsError) {
        console.error('Error fetching contractors:', contractorsError);
        throw contractorsError;
      }

      console.log('Fetched general contractors with "General Contractors":', contractorsData);

      // If no results with "General Contractors", try with "General Contracting"
      if (contractorsData && contractorsData.length === 0) {
        console.log('No results with "General Contractors", trying "General Contracting"');
        const { data: contractingData, error: contractingError } = await supabase
          .from('contractors')
          .select('*')
          .eq('service_type', 'General Contracting')
          .eq('location', 'oklahoma-city')
          .order('featured', { ascending: false })
          .order('rating', { ascending: false });

        if (contractingError) {
          console.error('Error fetching contractors with General Contracting:', contractingError);
          throw contractingError;
        }

        console.log('Fetched general contractors with "General Contracting":', contractingData);
        return contractingData as Contractor[];
      }

      return contractorsData as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-general-contractors-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established general contractors in Oklahoma City for residential and commercial construction services. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-general-contractors-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established general contractors in Oklahoma City for residential and commercial construction services. Free quotes, trusted professionals."
        canonicalUrl="/best-general-contractors-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable general contractors for all your residential and commercial construction needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our General Contractors</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our general contractors are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Project Management</h3>
                <p>Expert coordination of all aspects of your construction project from start to finish.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your construction projects from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Construction Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Custom home building</li>
                  <li>Home renovations and remodeling</li>
                  <li>Kitchen and bathroom remodels</li>
                  <li>Home additions and expansions</li>
                  <li>Basement finishing</li>
                  <li>Deck and patio construction</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Commercial building construction</li>
                  <li>Office renovations and build-outs</li>
                  <li>Retail space construction</li>
                  <li>Restaurant construction</li>
                  <li>Medical facility construction</li>
                  <li>Code compliance upgrades</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top General Contractors in Oklahoma City</h2>
          {contractors && contractors.length > 0 ? (
            <ContractorsList
              contractors={contractors}
              isLoading={isLoading}
              viewMode="grid"
              onViewModeChange={() => {}}
              onServiceTypeChange={() => {}}
              onLocationChange={() => {}}
              selectedServiceType="General Contractors"
            />
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of general contractors in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestGeneralContractorsPage;
